// src/services/api.js
export const API_URL = process.env.REACT_APP_API_URL || '';

export const authHeaders = () => {
    const token = localStorage.getItem('hoommyToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// =================== AUTH ===================

export const googleAuth = async (credential, rol = 'arrendatario') => {
    const response = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential, rol }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error con Google');
    return data;
};

export const registerUser = async (userData) => {
    const response = await fetch(`${API_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al registrar');
    return data;
};

export const completeRegistration = async (userData) => {
    const response = await fetch(`${API_URL}/api/register/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al completar registro');
    return data;
};

export const loginUser = async (email, password) => {
    const response = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al iniciar sesión');
    return data;
};

export const getUserByEmail = async (email) => {
    const response = await fetch(`${API_URL}/api/user-by-email?email=${encodeURIComponent(email)}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Usuario no encontrado');
    return data;
};

// =================== DASHBOARD ===================

export const getDashboardStats = async (userId, rol) => {
    const response = await fetch(`${API_URL}/api/dashboard/stats/${userId}/${rol}`, {
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener estadísticas del dashboard');
    return data;
};

// =================== PROPERTIES ===================

export const getProperties = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.tipo) params.append('tipo', filters.tipo);
    if (filters.ciudad) params.append('ciudad', filters.ciudad);
    if (filters.precio_min) params.append('precio_min', filters.precio_min);
    if (filters.precio_max) params.append('precio_max', filters.precio_max);
    if (filters.habitaciones) params.append('habitaciones', filters.habitaciones);
    if (filters.para_estudiantes) params.append('para_estudiantes', 'true');

    const response = await fetch(`${API_URL}/api/properties?${params.toString()}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener propiedades');
    return data;
};

export const getProperty = async (id) => {
    const response = await fetch(`${API_URL}/api/properties/${id}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener propiedad');
    return data;
};

export const getCities = async () => {
    const response = await fetch(`${API_URL}/api/properties/cities`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener ciudades');
    return data;
};

// =================== PROPERTY CRUD ===================

export const createProperty = async (propertyData) => {
    // If propertyData is FormData, don't set Content-Type so browser sets boundary
    const isFormData = propertyData instanceof FormData;

    const options = {
        method: 'POST',
        body: isFormData ? propertyData : JSON.stringify(propertyData),
    };

    if (!isFormData) {
        options.headers = { 'Content-Type': 'application/json', ...authHeaders() };
    } else {
        options.headers = { ...authHeaders() };
    }

    const response = await fetch(`${API_URL}/api/properties`, options);
    const data = await response.json();
    if (!response.ok) {
        // Si es un error de plan/limite, incluir upgrade_url para que el componente redirija
        if (data.error === 'plan_required' || data.error === 'limit_reached') {
            const err = new Error(data.message || 'Necesitas un plan activo');
            err.upgrade_url = data.upgrade_url;
            err.error_type = data.error;
            throw err;
        }
        throw new Error(data.error || 'Error al crear la propiedad');
    }
    return data;
};

export const getUserProperties = async (userId) => {
    const response = await fetch(`${API_URL}/api/properties/user/${userId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener tus propiedades');
    return data;
};

export const updateProperty = async (id, propertyData) => {
    const response = await fetch(`${API_URL}/api/properties/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(propertyData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al actualizar la propiedad');
    return data;
};

export const deleteProperty = async (id) => {
    const response = await fetch(`${API_URL}/api/properties/${id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al eliminar la propiedad');
    return data;
};

// =================== USER PROFILE ===================

export const getUserProfile = async (userId) => {
    const response = await fetch(`${API_URL}/api/user/${userId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener el perfil');
    return data;
};

export const updateUserProfile = async (userId, profileData) => {
    const response = await fetch(`${API_URL}/api/user/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(profileData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al actualizar el perfil');
    return data;
};

// =================== CONTRACTS ===================

export const getOwnerTemplates = async (userId) => {
    const response = await fetch(`${API_URL}/api/templates/owner/${userId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener las plantillas');
    return data;
};

export const getSystemTemplates = async () => {
    const response = await fetch(`${API_URL}/api/templates/default`);
    const data = await response.json();
    if (!response.ok) return [];
    // El endpoint devuelve array de plantillas o array de cláusulas (fallback antiguo)
    if (Array.isArray(data) && data.length > 0 && data[0].id) return data;
    return [];
};



export const uploadContractPDF = async (formData) => {
    const response = await fetch(`${API_URL}/api/contracts/upload_pdf`, {
        method: 'POST',
        body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al subir el PDF');
    return data;
};

export const createContract = async (contractData) => {
    const response = await fetch(`${API_URL}/api/contracts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(contractData),
    });
    const data = await response.json();
    if (!response.ok) {
        if (data.error === 'plan_required' || data.error === 'limit_reached') {
            const err = new Error(data.message || 'Necesitas un plan activo');
            err.upgrade_url = data.upgrade_url;
            err.error_type = data.error;
            throw err;
        }
        throw new Error(data.error || 'Error al crear el contrato');
    }
    return data;
};

export const updateContract = async (id, putData) => {
    const response = await fetch(`${API_URL}/api/contracts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(putData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al actualizar el contrato');
    return data;
};

export const getUserContracts = async (userId) => {
    const response = await fetch(`${API_URL}/api/contracts/user/${userId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener los contratos');
    return data;
};

export const getContract = async (contractId) => {
    const response = await fetch(`${API_URL}/api/contracts/${contractId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener el contrato');
    return data;
};

export const signContract = async (contractId, userId) => {
    const response = await fetch(`${API_URL}/api/contracts/${contractId}/sign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al firmar el contrato');
    return data;
};

export const deleteContract = async (contractId, userId) => {
    const response = await fetch(`${API_URL}/api/contracts/${contractId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ user_id: userId }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al eliminar el contrato');
    return data;
};

export const generateWhatsAppSignLink = async (contractId, landlordId) => {
    const response = await fetch(`${API_URL}/api/contracts/${contractId}/generate-sign-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ landlord_id: landlordId }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al generar el enlace de firma');
    return data;
};

export const uploadSignedPDF = async (contractId, file) => {
    const formData = new FormData();
    formData.append('pdf', file);
    const response = await fetch(`${API_URL}/api/contracts/${contractId}/upload-signed-pdf`, {
        method: 'POST',
        body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al subir el PDF firmado');
    return data;
};

export const getContractByToken = async (token) => {
    const response = await fetch(`${API_URL}/api/contracts/quick-sign/${token}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Enlace inválido o expirado');
    return data;
};

export const signContractByToken = async (token) => {
    const response = await fetch(`${API_URL}/api/contracts/quick-sign/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al firmar el contrato');
    return data;
};

// =================== CHAT ===================

export const getUserConversations = async (userId) => {
    const response = await fetch(`${API_URL}/api/chat/conversations/${userId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener las conversaciones');
    return data;
};


export const startConversation = async (propiedadId, inquilinoId, arrendadorId) => {
    const response = await fetch(`${API_URL}/api/chat/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
            propiedad_id: propiedadId,
            inquilino_id: inquilinoId,
            arrendador_id: arrendadorId
        }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al iniciar la conversación');
    return data;
};

export const getConversationMessages = async (conversationId, sinceId = null) => {
    const url = sinceId
        ? `${API_URL}/api/chat/${conversationId}/messages?since=${sinceId}`
        : `${API_URL}/api/chat/${conversationId}/messages`;
    const response = await fetch(url);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener los mensajes');
    return data;
};

export const getUnreadMessageCount = async (userId) => {
    const response = await fetch(`${API_URL}/api/chat/unread-count/${userId}`);
    const data = await response.json();
    if (!response.ok) return { unread_count: 0 };
    return data;
};

export const sendMessage = async (conversationId, remitenteId, contenido) => {
    const response = await fetch(`${API_URL}/api/chat/${conversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
            remitente_id: remitenteId,
            contenido: contenido
        }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al enviar el mensaje');
    return data;
};

// =================== PAGOS (WOMPI) ===================

export const createPayment = async (paymentData) => {
    const response = await fetch(`${API_URL}/api/payments/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(paymentData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al generar el pago');
    return data;
};

export const verifyPayment = async (referencia) => {
    const response = await fetch(`${API_URL}/api/payments/verify/${referencia}`, {
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Pago no encontrado');
    return data;
};

export const getContractPayments = async (contratoId) => {
    const response = await fetch(`${API_URL}/api/payments/contract/${contratoId}`, {
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener pagos');
    return data;
};

// =================== SALDO ARRENDADOR ===================

export const getLandlordBalance = async (userId) => {
    const response = await fetch(`${API_URL}/api/payments/balance/${userId}`, {
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener saldo');
    return data;
};

export const getBalanceMovements = async (userId) => {
    const response = await fetch(`${API_URL}/api/payments/balance/${userId}/movements`, {
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener movimientos');
    return data;
};

export const requestWithdrawal = async (withdrawalData) => {
    const response = await fetch(`${API_URL}/api/payments/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(withdrawalData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al solicitar retiro');
    return data;
};

// =================== PROPIEDADES — Galería & Mapa ===================

export const getPropertyImages = async (propId) => {
    const response = await fetch(`${API_URL}/api/properties/${propId}/images`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener imágenes');
    return data;
};

export const uploadPropertyImages = async (propId, files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('imagenes', f));
    const response = await fetch(`${API_URL}/api/properties/${propId}/images`, {
        method: 'POST',
        headers: { ...authHeaders() },
        body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al subir imágenes');
    return data;
};

export const deletePropertyImage = async (propId, imgId) => {
    const response = await fetch(`${API_URL}/api/properties/${propId}/images/${imgId}`, {
        method: 'DELETE',
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al eliminar imagen');
    return data;
};

export const getMapProperties = async () => {
    const response = await fetch(`${API_URL}/api/properties/map`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al cargar el mapa');
    return data;
};

// =================== STUDENT VERIFICATION ===================

export const submitStudentVerification = async (formData) => {
    const response = await fetch(`${API_URL}/api/students/verify`, {
        method: 'POST',
        body: formData, // FormData obj as it contains file
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al enviar verificación');
    return data;
};

export const getStudentVerificationStatus = async (userId) => {
    const response = await fetch(`${API_URL}/api/students/status/${userId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener estado');
    return data;
};

export const getAllStudentVerifications = async () => {
    const response = await fetch(`${API_URL}/api/students/admin/requests`, {
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener solicitudes');
    return data;
};

export const processStudentVerification = async (reqId, action, comentarios = '') => {
    const response = await fetch(`${API_URL}/api/students/admin/${reqId}/${action}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ comentarios_admin: comentarios })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || `Error al procesar solicitud (${action})`);
    return data;
};

// =================== MAINTENANCE REQUESTS ===================

export const getMaintenanceByOwner = async (userId) => {
    const response = await fetch(`${API_URL}/api/maintenance/owner/${userId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener solicitudes de mantenimiento');
    return data;
};

// =================== MONETIZACION DE MANTENIMIENTO ===================

export const hireHoommySpecialist = async (requestId, propietarioId, serviceData = {}) => {
    const response = await fetch(`${API_URL}/api/maintenance/${requestId}/hire_hoommy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ propietario_id: propietarioId, ...serviceData })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al contratar especialista');
    return data;
};

export const getServiceOrder = async (requestId) => {
    const response = await fetch(`${API_URL}/api/maintenance/service_order/${requestId}`);
    const data = await response.json();
    if (!response.ok) {
        if (response.status === 404) return null; // Expected if no order exists
        throw new Error(data.error || 'Error al obtener orden de servicio');
    }
    return data;
};

export const payServiceOrder = async (orderId, transaccionId) => {
    const response = await fetch(`${API_URL}/api/maintenance/service_order/${orderId}/pay`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transaccion_id: transaccionId })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al procesar pago de servicio');
    return data;
};
// =================== SERVICE CATALOG & PROVIDERS ===================

export const getServiceCatalog = async () => {
    const response = await fetch(`${API_URL}/api/maintenance/catalog`);
    return response.json();
};

export const getEarnings = async () => {
    const response = await fetch(`${API_URL}/api/maintenance/earnings`);
    return response.json();
};

export const getProviders = async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_URL}/api/providers?${params}`);
    return response.json();
};

export const createProvider = async (providerData) => {
    const response = await fetch(`${API_URL}/api/providers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(providerData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error);
    return data;
};

export const updateProvider = async (id, providerData) => {
    const response = await fetch(`${API_URL}/api/providers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(providerData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error);
    return data;
};

export const deleteProvider = async (id) => {
    const response = await fetch(`${API_URL}/api/providers/${id}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error);
    return data;
};

// =================== TEMPLATES & AI ===================

export const getDefaultTemplate = async () => {
    const response = await fetch(`${API_URL}/api/templates/default`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener la plantilla por defecto');
    return data;
};



export const createTemplate = async (templateData) => {
    const response = await fetch(`${API_URL}/api/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al crear plantilla');
    return data;
};

export const updateTemplate = async (templateId, templateData) => {
    const response = await fetch(`${API_URL}/api/templates/${templateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al actualizar plantilla');
    return data;
};

export const deleteTemplate = async (templateId) => {
    const response = await fetch(`${API_URL}/api/templates/${templateId}`, {
        method: 'DELETE'
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al eliminar plantilla');
    return data;
};

export const generateAITemplate = async (prompt, inventario) => {
    const response = await fetch(`${API_URL}/api/templates/generate_ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, inventario })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al generar contrato con IA');
    return data;
};

// =================== ANALYTICS ===================

export const getOwnerAnalytics = async (userId) => {
    const response = await fetch(`${API_URL}/api/analytics/owner/${userId}`, {
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener las analíticas');
    return data;
};

// =================== AI ===================

export const aiCompareProperties = async (properties) => {
    const response = await fetch(`${API_URL}/api/ai/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ properties }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al analizar propiedades');
    return data;
};

export const aiChat = async (message, history = [], userId = null) => {
    const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, history, user_id: userId }),
    });
    const data = await response.json();
    if (!response.ok) {
        if (data.error === 'plan_required' || data.error === 'ia_limit_reached') {
            const err = new Error(data.message || 'Necesitas un plan para usar la IA');
            err.upgrade_url = data.upgrade_url;
            throw err;
        }
        throw new Error(data.error || 'Error del asistente IA');
    }
    return data;
};

export const aiGenerateDescription = async (propertyData) => {
    const response = await fetch(`${API_URL}/api/ai/describe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(propertyData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al generar descripción');
    return data;
};

// =================== FAVORITOS ===================

export const getUserFavorites = async (userId) => {
    const response = await fetch(`${API_URL}/api/favorites/${userId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener favoritos');
    return data;
};

export const toggleFavorite = async (userId, propertyId) => {
    const response = await fetch(`${API_URL}/api/favorites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ user_id: userId, property_id: propertyId }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al cambiar favorito');
    return data;
};

// =================== VISITAS ===================

export const createVisit = async (visitData) => {
    const response = await fetch(`${API_URL}/api/visits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(visitData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al solicitar visita');
    return data;
};

export const getUserVisits = async (userId) => {
    const response = await fetch(`${API_URL}/api/visits/user/${userId}`, {
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener visitas');
    return data;
};

export const updateVisitStatus = async (visitId, statusData) => {
    const response = await fetch(`${API_URL}/api/visits/${visitId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(statusData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al actualizar visita');
    return data;
};

// =================== RESEÑAS / CALIFICACIONES ===================

export const getUserReviews = async (userId) => {
    const response = await fetch(`${API_URL}/api/reviews/${userId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener reseñas');
    return data;
};

export const createReview = async (reviewData) => {
    const response = await fetch(`${API_URL}/api/reviews/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(reviewData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al crear reseña');
    return data;
};

// =================== PLANES Y SUSCRIPCIONES ===================

export const getPlans = async () => {
    const response = await fetch(`${API_URL}/api/plans`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener planes');
    return data;
};

export const getUserSubscription = async (userId) => {
    const response = await fetch(`${API_URL}/api/subscriptions/${userId}`, {
        headers: { ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener suscripcion');
    return data;
};

export const createCheckoutSession = async (userId, plan, periodo = 'mensual') => {
    const response = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ user_id: userId, plan, periodo }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al crear sesion de pago');
    return data;
};

export const activatePlan = async (userId, plan) => {
    const response = await fetch(`${API_URL}/api/subscriptions/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ user_id: userId, plan }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al activar plan');
    return data;
};

// =================== INVENTARIOS ===================

export const getContractInventory = async (contractId) => {
    const response = await fetch(`${API_URL}/api/inventory/contract/${contractId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener inventario');
    return data;
};

export const createInventory = async (inventoryData) => {
    const response = await fetch(`${API_URL}/api/inventory/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inventoryData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al crear inventario');
    return data;
};

export const addInventoryItem = async (inventoryId, itemData) => {
    const response = await fetch(`${API_URL}/api/inventory/${inventoryId}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al añadir ítem');
    return data;
};

export const deleteInventoryItem = async (inventoryId, itemId) => {
    const response = await fetch(`${API_URL}/api/inventory/${inventoryId}/items/${itemId}`, {
        method: 'DELETE',
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al eliminar ítem');
    return data;
};

export const signInventory = async (inventoryId, signData) => {
    const response = await fetch(`${API_URL}/api/inventory/${inventoryId}/sign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al firmar inventario');
    return data;
};

// =================== AGENCIA (INMOBILIARIA) ===================

export const getAgencyBySlug = async (slug) => {
    const response = await fetch(`${API_URL}/api/agency/slug/${slug}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Agencia no encontrada');
    return data;
};

export const updateAgencyProfile = async (userId, profileData) => {
    const response = await fetch(`${API_URL}/api/agency/${userId}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al actualizar perfil');
    return data;
};

export const uploadAgencyLogo = async (userId, file) => {
    const fd = new FormData();
    fd.append('logo', file);
    const response = await fetch(`${API_URL}/api/agency/${userId}/logo`, { method: 'POST', body: fd });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al subir logo');
    return data;
};

export const generateAgencySlug = async (userId) => {
    const response = await fetch(`${API_URL}/api/agency/${userId}/generate-slug`, { method: 'POST' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al generar slug');
    return data;
};

// =================== AGENTES ===================

export const getAgents = async (inmobiliariaId) => {
    const response = await fetch(`${API_URL}/api/agents/${inmobiliariaId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener agentes');
    return data;
};

export const createAgent = async (inmobiliariaId, agentData) => {
    const response = await fetch(`${API_URL}/api/agents/${inmobiliariaId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(agentData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al crear agente');
    return data;
};

export const updateAgent = async (agentId, agentData) => {
    const response = await fetch(`${API_URL}/api/agents/item/${agentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(agentData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al actualizar agente');
    return data;
};

export const deleteAgent = async (agentId) => {
    const response = await fetch(`${API_URL}/api/agents/item/${agentId}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al eliminar agente');
    return data;
};

// =================== CRM / LEADS ===================

export const getCRMLeads = async (inmobiliariaId, filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_URL}/api/crm/${inmobiliariaId}/leads?${params}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener leads');
    return data;
};

export const createLead = async (inmobiliariaId, leadData) => {
    const response = await fetch(`${API_URL}/api/crm/${inmobiliariaId}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al crear lead');
    return data;
};

export const updateLead = async (leadId, leadData) => {
    const response = await fetch(`${API_URL}/api/crm/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al actualizar lead');
    return data;
};

export const deleteLead = async (leadId) => {
    const response = await fetch(`${API_URL}/api/crm/leads/${leadId}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al eliminar lead');
    return data;
};

export const getCRMStats = async (inmobiliariaId) => {
    const response = await fetch(`${API_URL}/api/crm/${inmobiliariaId}/leads/stats`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener estadísticas CRM');
    return data;
};

// =================== IMPORTACIÓN MASIVA ===================

export const bulkImportProperties = async (userId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('propietario_id', userId);
    const response = await fetch(`${API_URL}/api/properties/bulk-import`, { method: 'POST', body: fd });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error en importación');
    return data;
};

export const getBulkImportHistory = async (userId) => {
    const response = await fetch(`${API_URL}/api/properties/bulk-import/history/${userId}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener historial');
    return data;
};

// =================== REPORTES ===================

export const getReportsOverview = async (inmobiliariaId) => {
    const response = await fetch(`${API_URL}/api/reports/${inmobiliariaId}/overview`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener resumen');
    return data;
};

export const getPropertyReport = async (inmobiliariaId) => {
    const response = await fetch(`${API_URL}/api/reports/${inmobiliariaId}/properties`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener reporte de propiedades');
    return data;
};

export const getAgentReport = async (inmobiliariaId) => {
    const response = await fetch(`${API_URL}/api/reports/${inmobiliariaId}/agents`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener reporte de agentes');
    return data;
};

export const getRevenueChart = async (inmobiliariaId) => {
    const response = await fetch(`${API_URL}/api/reports/${inmobiliariaId}/revenue`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Error al obtener ingresos');
    return data;
};

// =================== TOURS 360° ===================

export const createTour = async (propertyId) => {
    const res = await fetch(`${API_URL}/api/tours`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ property_id: propertyId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al crear tour');
    return data;
};

export const addTourRoom = async (tourId, roomType, name, order) => {
    const res = await fetch(`${API_URL}/api/tours/${tourId}/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ room_type: roomType, name, order }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al agregar cuarto');
    return data; // { room_id, pattern }
};

export const uploadRoomPhotos = async (tourId, roomId, photoBlobs) => {
    const formData = new FormData();
    photoBlobs.forEach((blob, i) => {
        formData.append('photos', blob, `photo_${i}.jpg`);
    });
    const res = await fetch(`${API_URL}/api/tours/${tourId}/rooms/${roomId}/photos`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al subir fotos');
    return data;
};

export const stitchRoom = async (tourId, roomId) => {
    const res = await fetch(`${API_URL}/api/tours/${tourId}/rooms/${roomId}/stitch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al iniciar stitching');
    return data;
};

export const getRoomStatus = async (tourId, roomId) => {
    const res = await fetch(`${API_URL}/api/tours/${tourId}/rooms/${roomId}/status`, {
        headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al obtener estado');
    return data; // { status, panorama_url }
};

export const publishTour = async (tourId) => {
    const res = await fetch(`${API_URL}/api/tours/${tourId}/publish`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al publicar tour');
    return data;
};

export const getPropertyTour = async (propertyId) => {
    const res = await fetch(`${API_URL}/api/properties/${propertyId}/tour`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al cargar tour');
    return data; // { tour: { id, rooms: [...] } | null }
};
